import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` },
});

// The two statuses an email order moves through. The server maps them onto the order
// status enum ('accepted' → confirmed, which the IT stage badges render as "Order
// Placed"); the labels here are what staff actually recognise.
const STATUS_OPTS = [
  { value: "accepted", label: "Order Accepted" },
  { value: "shipped", label: "Order Shipped" },
];

// Inverse of the server's EMAIL_ORDER_STATUS map (emailOrder.constants.js): the order
// stores 'confirmed', the popup speaks 'accepted'.
const optionForOrder = (order) => {
  if (!order) return "";
  if (order.status === "shipped") return "shipped";
  if (order.status === "confirmed") return "accepted";
  return "";
};

const labelFor = (value) =>
  STATUS_OPTS.find((o) => o.value === value)?.label || "";

/**
 * "Email Order" popup — Infinity Touch email ordering, in two modes.
 *
 * Some orders arrive by email or phone instead of through the app or the customer
 * portal. They are recorded against a case as $0 itemless tracking orders, and a case
 * may carry SEVERAL of them. The name describes where the order came FROM; since TR-30
 * the server does also tell the funeral home when one moves.
 *
 * `order` null  → create. "New Email Order" opens a brand-new one; it never touches
 *                 any email order already on the case, which is what the old upsert
 *                 did — logging a second order silently rewrote the first.
 * `order` set   → update. The card's own "Update Status" button, scoped by order_id.
 *
 * The status seeds from the order rather than clearing, so the popup always shows where
 * that order actually stands: an admin who had just moved one to Shipped got no
 * confirmation it had stuck, reopened a blank dialog and re-submitted "Order Accepted",
 * knocking it back (TR-30 1.2).
 */
export default function EmailOrderDialog({ open, onClose, memorialRecordId, order, onSaved }) {
  const [status, setStatus] = useState("");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  const current = optionForOrder(order);
  const isUpdate = Boolean(order);

  // Reset on every open so a previous submission's remark is never resubmitted.
  useEffect(() => {
    if (open) {
      setStatus(optionForOrder(order));
      setRemarks("");
    }
  }, [open, order]);

  const save = async () => {
    if (!status) {
      toast.error("Choose a status for the email order");
      return;
    }
    setSaving(true);
    try {
      const res = isUpdate
        ? await axios.put(
            `${API}/admin/orders/${order.order_id}/email-order-status`,
            { status, remarks },
            getAuthHeaders()
          )
        : await axios.post(
            `${API}/admin/memorial-records/${memorialRecordId}/email-order`,
            { status, remarks },
            getAuthHeaders()
          );
      toast.success(res.data?.message || "Email order saved");
      onClose?.();
      onSaved?.(res.data?.order);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to save email order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isUpdate ? "Update Email Order" : "New Email Order"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {isUpdate ? (
            <p className="text-sm text-slate-600 rounded-lg bg-slate-50 border px-3 py-2">
              Order <span className="font-semibold">{order.order_number}</span> is
              currently <span className="font-semibold text-slate-900">{labelFor(current) || order.status}</span>.
              Choosing a different status below will move it.
            </p>
          ) : (
            // Says plainly that this opens another one, so an admin recording a second
            // phoned-in order is not left wondering whether it overwrites the first.
            <p className="text-sm text-slate-600 rounded-lg bg-slate-50 border px-3 py-2">
              Records a new order taken over email or phone against this case. Any email
              orders already on the case are left untouched.
            </p>
          )}

          <div className="space-y-1.5">
            <Label>Email Order Status</Label>
            {/* Radix rejects "" as a SelectItem value, so the empty state lives in the
                placeholder rather than as an option. */}
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Choose status for email order" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            {/* The legacy popup labelled this "Email Order Status" too — that was a bug
                in the old admin and is not reproduced. */}
            <Label>Remarks</Label>
            <Textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Optional remark — shown in the order timeline"
              className="min-h-[90px]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose?.()} disabled={saving}>Cancel</Button>
          <Button
            onClick={save}
            disabled={saving || !status}
            className="bg-green-600 hover:bg-green-700"
          >
            {saving ? "Saving…" : isUpdate ? "Update Order" : "Create Order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
